import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { useCurrency } from '../lib/currency-context'
import { fmtMoney } from '../lib/money'
import { useTranslation } from 'react-i18next'
import { useAuth, authHeaders } from '../lib/auth'
import { RebillEvidence } from '../components/RebillEvidence'
import { RecordHistory } from '../components/RecordHistory'
import { DisputeReplies } from '../components/CarrierReplies'

interface Dispute {
  id: string
  dispute_id: number
  carrier: string
  invoice_number: string
  tracking: string
  type: string
  code: string
  detail: string
  at_stake_minor: number
  credit_minor: number
  status: 'DRAFT' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'CREDITED' | 'WITHDRAWN'
  submitted_at: string | null
  resolved_at: string | null
  detected_at: string
  notes?: string | null      // our internal notes (never sent to the carrier)
  detected_by?: string | null
  submitted_by?: string | null   // from the activity log - not recorded before 2026-10-25
  resolved_by?: string | null
  case_ref?: string | null       // carrier case / reference number (migration 079)
  submitted_via?: 'email' | 'portal' | 'phone' | 'other' | null
  replies_new?: number          // carrier e-mails about it, not reviewed yet (migration 080)
  credits_new?: number          // carrier credits found, not decided yet (migration 078)
  credits_new_minor?: number
  // the held rebill this dispute controls (may be null for older/other disputes)
  rebill_id: number | null
  rebill_customer: string | null
  rebill_amount_minor: number | null
  rebill_status: string | null
  customer_upheld?: number   // rule 5: earlier disputes of this customer the carrier upheld
  deadline?: string | null   // rule 6: last day to file (invoice date + carrier's dispute days)
  days_left?: number | null
  handling_case?: 'under' | 'over' | null   // rule 3: declared box vs carrier handling limits
  rules_confirmed?: boolean | null
  ship_date?: string | null      // label / ship date
  invoice_date?: string | null   // carrier invoice date (from the held rebill)
}

interface Summary {
  kpis: {
    open:     { n: number; total_minor: number }
    won:      { n: number; total_minor: number }
    lost:     { n: number; total_minor: number }
    credited: { n: number; total_minor: number }
    withdrawn?: { n: number; total_minor: number }   // not disputed — not a loss
  }
  lifecycle?: { stage: string; n: number }[]
  categoryWins?: { category: string; decided: number; won: number; losing: boolean }[]
}

// Dispute rules (Sahin 2026-10-23)
const SMALL_DISPUTE_MINOR = 300    // rule 1: under $3 is not worth a dispute
const FOLLOW_UP_DAYS = 14          // rule 8: chase the carrier ...
const ESCALATE_DAYS = 30           //         ... and escalate
const SEND_DAY = 1                 // rule 7: weekly batch on Monday (0 = Sunday)
const DEADLINE_WARN_DAYS = 15      // rule 6: warn this many days before the filing deadline

const STATUS_FILTERS = [
  // Open (default) = still being worked: Draft + Submitted; closed / decided ones
  // are under Resolved, and All shows everything (Sahin 2026-10-25)
  { label: 'Open',      value: 'OPEN' },
  { label: 'All',       value: 'ALL' },
  { label: 'Draft',     value: 'DRAFT' },
  { label: 'Submitted', value: 'SUBMITTED' },
  // RESOLVED is a sentinel the API expands to ACCEPTED/REJECTED/CREDITED/
  // WITHDRAWN — a bare 'ACCEPTED' filter would hide the others.
  { label: 'Resolved',  value: 'RESOLVED' },
]

const RESOLVED = ['ACCEPTED', 'REJECTED', 'CREDITED', 'WITHDRAWN']

// Disputes list filters; keys are the API query names (GET /api/disputes)
interface ListFilter {
  carrier: string; category: string; customer: string; q: string
  amount_min: string; amount_max: string; age_min: string; age_max: string
  follow_up: '' | '1'   // rule 8: submitted 14+ days, no answer
  credit: '' | '1'      // a carrier credit was found, waiting for review
  replied: '' | '1'     // a carrier e-mail about it waits for review
  deadline: '' | 'soon' | 'passed'   // rule 6: draft due within 15 days / already past its filing deadline
  ship_from: string; ship_to: string   // the Shipped column, YYYY-MM-DD
}
const EMPTY_LIST_FILTER: ListFilter = {
  carrier: '', category: '', customer: '', q: '', amount_min: '', amount_max: '', age_min: '', age_max: '', follow_up: '', credit: '', replied: '', deadline: '', ship_from: '', ship_to: '',
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  )
}

// Static Tailwind classes — must be literal tokens or the JIT scanner purges
// them (an interpolated `text-${tone}-700` never reaches the bundle).
const TONE_TEXT: Record<string, string> = {
  ink: 'text-ink-700', flame: 'text-flame-700', royal: 'text-royal-700',
  mint: 'text-mint-700', sky2: 'text-sky2-700',
}

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—'
}
function daysSince(iso: string | null): number | null {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
}
function statusPill(s: string): string {
  return s === 'DRAFT' ? 'pill-ink'
    : s === 'SUBMITTED' ? 'pill-royal'
    : s === 'ACCEPTED' ? 'pill-mint'
    : s === 'REJECTED' ? 'pill-flame'
    : s === 'WITHDRAWN' ? 'pill-ink'
    : 'pill-sky'
}
// What the operator sees, in plain words rather than the DB status name.
function statusLabel(s: string): string {
  return s === 'ACCEPTED' ? 'WAIVED'
    : s === 'REJECTED' ? 'UPHELD'
    : s === 'CREDITED' ? 'PARTIAL'
    : s === 'WITHDRAWN' ? 'NOT DISPUTED'
    : s
}

// embedded: shown as the Disputes tab of Carrier Recovery (no page title)
export function DisputesView({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { can } = useAuth()
  const qc = useQueryClient()
  // Acting on a dispute needs the resolve_disputes permission - the same rule
  // the API enforces - not the admin user type (Sahin 2026-10-21).
  const canResolve = can('resolve_disputes')
  const [filter, setFilter] = useState('OPEN')
  const [showCreate, setShowCreate] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [exporting, setExporting] = useState<null | 'carrier' | 'internal'>(null)
  // Summarized (support's filing set) or Extended (every column)
  const [exportDetail, setExportDetail] = useState<'summary' | 'full'>('summary')
  const canInternal = can('resolve_disputes')
  // DRAFT disputes ticked for "Send to carrier", and the ones in the open send dialog
  const [picked, setPicked] = useState<Set<number>>(new Set())
  const [sendIds, setSendIds] = useState<number[] | null>(null)
  const lastPicked = useRef<number | null>(null)   // anchor for shift-click ranges

  // close the Export menu on Escape
  useEffect(() => {
    if (!exportOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExportOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [exportOpen])

  // Excel of the disputes on the current tab. 'carrier' = safe to send to the
  // carrier (no customer / rebill / profit); 'internal' = everything.
  async function exportExcel(mode: 'carrier' | 'internal') {
    setExportOpen(false)
    setExporting(mode)
    let url: string | null = null
    try {
      const res = await fetch(`/api/disputes/export?stage=${filter}&mode=${mode}&detail=${exportDetail}`)
      if (!res.ok) {
        const msg = await res.json().catch(() => null)
        throw new Error(msg?.error || `Export failed (${res.status})`)
      }
      const blob = await res.blob()
      const cd = res.headers.get('Content-Disposition') ?? ''
      const name = /filename="([^"]+)"/.exec(cd)?.[1]
        ?? `disputes-${filter.toLowerCase()}-${mode}-${new Date().toISOString().slice(0, 10)}.xlsx`
      url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = name; a.click()
    } catch (e) {
      alert((e as Error).message)
    } finally {
      // revoke later: revoking right after click() cancels the download in some browsers
      if (url) { const u = url; setTimeout(() => URL.revokeObjectURL(u), 10_000) }
      setExporting(null)
    }
  }

  // filter bar: edit the draft, Apply copies it to `applied` (the query key)
  const [draftF, setDraftF] = useState<ListFilter>(EMPTY_LIST_FILTER)
  const [appliedF, setAppliedF] = useState<ListFilter>(EMPTY_LIST_FILTER)
  const listParams = new URLSearchParams({ stage: filter, limit: '1000' })
  Object.entries(appliedF).forEach(([k, v]) => { if (v) listParams.set(k, v) })
  const { data: list, isLoading } = useQuery<{
    disputes: Dispute[]; total?: number; total_minor?: number
    facets?: { categories: string[]; customers: string[] }
  }>({
    queryKey: ['disputes', filter, appliedF],
    queryFn: () => fetch(`/api/disputes?${listParams}`).then(r => r.json()),
  })
  const applyF = () => { setAppliedF({ ...draftF }); setPicked(new Set()) }

  // The big search box (Sahin 2026-10-25): tracking, invoice or carrier case
  // number, searched as you type (0.3 s pause) - the same `q` filter as the
  // filter bar, kept in step with it.
  const [bigQ, setBigQ] = useState('')
  useEffect(() => { setBigQ(appliedF.q) }, [appliedF.q])
  useEffect(() => {
    if (bigQ === appliedF.q) return
    const t = setTimeout(() => {
      setDraftF(p => ({ ...p, q: bigQ }))
      setAppliedF(p => ({ ...p, q: bigQ }))
      setPicked(new Set())
    }, 300)
    return () => clearTimeout(t)
  }, [bigQ]) // eslint-disable-line react-hooks/exhaustive-deps
  const clearF = () => { setDraftF(EMPTY_LIST_FILTER); setAppliedF(EMPTY_LIST_FILTER); setPicked(new Set()) }
  const anyF = Object.values(appliedF).some(Boolean)
  const { data: summary } = useQuery<Summary>({
    queryKey: ['disputes-summary'],
    queryFn: () => fetch('/api/disputes/summary').then(r => r.json()),
  })

  const k = summary?.kpis
  // rule 9: categories carriers keep upholding
  const losing = (summary?.categoryWins ?? []).filter(c => c.losing)
  const losingCats = new Set(losing.map(c => c.category))
  // rule 7: weekly batch - drafts waiting, highlighted on send day
  const draftCount = summary?.lifecycle?.find(l => l.stage === 'Drafted')?.n ?? 0
  const sendDay = new Date().getDay() === SEND_DAY
  const [selectDraftsWhenLoaded, setSelectDraftsWhenLoaded] = useState(false)
  const selected = list?.disputes.find(d => d.dispute_id === detailId) ?? null
  const drafts = (list?.disputes ?? []).filter(d => d.status === 'DRAFT')
  const pickedNow = drafts.filter(d => picked.has(d.dispute_id)).map(d => d.dispute_id)
  const allPicked = drafts.length > 0 && drafts.every(d => picked.has(d.dispute_id))

  // bulk "Don't dispute": ticked drafts -> Not disputed; held customer charges are released (billed)
  const [confirmWithdrawAll, setConfirmWithdrawAll] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [withdrawMsg, setWithdrawMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const pickedDrafts = drafts.filter(d => picked.has(d.dispute_id))
  const releasing = pickedDrafts.filter(d => d.rebill_id != null && d.rebill_status === 'DISPUTED')
  const releaseCount = releasing.length
  const releaseMinor = releasing.reduce((s, d) => s + (d.rebill_amount_minor ?? 0), 0)
  useEffect(() => { setConfirmWithdrawAll(false) }, [picked, filter, appliedF])
  async function withdrawPicked() {
    setWithdrawing(true)
    try {
      const r = await fetch('/api/disputes/withdraw-bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dispute_ids: pickedNow, confirm: true }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.ok) {
        setWithdrawMsg({ ok: false, text: (j.error ?? `HTTP ${r.status}`) + (j.problems ? ': ' + j.problems.join('; ') : '') })
      } else {
        setWithdrawMsg({ ok: true, text: `Closed ${j.withdrawn} dispute(s) as not disputed`
          + (j.rebills_released ? `; ${j.rebills_released} customer charge(s) released for billing ($${Number(j.released_amount).toFixed(2)}).` : '.') })
        setPicked(new Set())
      }
    } catch (e) {
      setWithdrawMsg({ ok: false, text: `Could not close the disputes: ${(e as Error).message}. Nothing may have changed - reload and check.` })
    } finally {
      setWithdrawing(false); setConfirmWithdrawAll(false)
      qc.invalidateQueries({ queryKey: ['disputes'] })
      qc.invalidateQueries({ queryKey: ['disputes-summary'] })
    }
  }
  // bulk "Mark as submitted": ticked drafts filed together on a carrier portal / by phone (Veysel 2026-10-25)
  const [bulkSubmit, setBulkSubmit] = useState(false)
  const [bulkVia, setBulkVia] = useState<'portal' | 'phone' | 'other'>('portal')
  const [bulkCase, setBulkCase] = useState('')
  const [bulkSaving, setBulkSaving] = useState(false)
  useEffect(() => { setBulkSubmit(false) }, [picked, filter, appliedF])
  const pickedCarriers = [...new Set(pickedDrafts.map(d => d.carrier.toUpperCase()))]
  async function markPickedSubmitted() {
    setBulkSaving(true)
    try {
      const r = await fetch('/api/disputes/mark-submitted-bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dispute_ids: pickedNow, via: bulkVia, case_ref: bulkCase, confirm: true }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.ok) {
        setWithdrawMsg({ ok: false, text: (j.error ?? `HTTP ${r.status}`) + (j.problems ? ': ' + j.problems.join('; ') : '') })
      } else {
        setWithdrawMsg({ ok: true, text: `Marked ${j.submitted} dispute(s) as submitted${bulkCase.trim() ? ` with case ${bulkCase.trim()}` : ''}.` })
        setPicked(new Set()); setBulkCase('')
      }
    } catch (e) {
      setWithdrawMsg({ ok: false, text: `Could not mark the disputes: ${(e as Error).message}. Nothing may have changed - reload and check.` })
    } finally {
      setBulkSaving(false); setBulkSubmit(false)
      qc.invalidateQueries({ queryKey: ['disputes'] })
      qc.invalidateQueries({ queryKey: ['disputes-summary'] })
    }
  }
  // "Select all drafts" from the weekly batch bar: tick them once the Draft tab has loaded
  useEffect(() => {
    if (selectDraftsWhenLoaded && filter === 'DRAFT' && list && !isLoading) {
      setPicked(new Set(drafts.map(d => d.dispute_id)))
      setSelectDraftsWhenLoaded(false)
    }
  }, [selectDraftsWhenLoaded, filter, list, isLoading])  // eslint-disable-line react-hooks/exhaustive-deps
  // shift-click ticks / unticks every draft between the last click and this one
  const togglePick = (id: number, shift = false) => {
    const order = drafts.map(d => d.dispute_id)
    const a = shift && lastPicked.current != null ? order.indexOf(lastPicked.current) : -1
    const b = order.indexOf(id)
    const ids = a >= 0 && b >= 0 ? order.slice(Math.min(a, b), Math.max(a, b) + 1) : [id]
    setPicked(prev => {
      const next = new Set(prev); const on = !next.has(id)
      ids.forEach(x => on ? next.add(x) : next.delete(x))
      return next
    })
    lastPicked.current = id
  }

  // one compact stat
  function Stat({ label, value, sub, tone = 'ink' }:
    { label: string; value: string; sub?: string; tone?: string }) {
    return (
      <div className="flex-1 px-4 py-3">
        <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold">{label}</div>
        <div className={`text-[22px] font-bold mt-0.5 ${TONE_TEXT[tone] ?? TONE_TEXT.ink}`}>{value}</div>
        {sub && <div className="text-[11.5px] text-ink-500 mt-0.5">{sub}</div>}
      </div>
    )
  }

  return (
    <div>
      {!embedded && (
        <div className="mb-5">
          <div className="text-[12px] font-semibold text-flame-600 uppercase tracking-wider">Module 3 · Carrier-side</div>
          <h1 className="text-[28px] font-bold tracking-tight mt-1">{t('disputes.title')}</h1>
          <p className="text-ink-500 text-[13px] mt-1">Money to recover from carriers. Work each dispute → submit to the carrier → record their decision.</p>
        </div>
      )}

      {/* slim stat strip — replaces the four big cards + charts. Not on Carrier
          Recovery: its own strip at the top already shows these (embedded). */}
      {!embedded && <Card className="mb-4 flex divide-x divide-ink-100">
        <Stat label="Open" value={String(k?.open.n ?? 0)}
              sub={fmtMoney(k?.open.total_minor ?? 0, currency) + ' at stake'} tone="flame" />
        <Stat label="Awaiting carrier"
              value={String(list?.disputes.filter(d => d.status === 'SUBMITTED').length ?? 0)}
              sub="submitted, no decision yet" tone="royal" />
        <Stat label="Recovered" value={String(k?.won.n ?? 0)}
              sub={fmtMoney(k?.won.total_minor ?? 0, currency) + ' waived'} tone="mint" />
        <Stat label="Partial credit" value={String(k?.credited.n ?? 0)}
              sub={fmtMoney(k?.credited.total_minor ?? 0, currency) + ' credited'} tone="sky2" />
        <Stat label="Win rate"
              value={k && (k.won.n + k.lost.n) > 0 ? `${Math.round(k.won.n / (k.won.n + k.lost.n) * 100)}%` : '—'}
              sub="waived / decided" tone="ink" />
      </Card>}

      {/* the work — table first */}
      {/* overflow-visible: the Export menu drops below the header and must not be clipped on short lists */}
      <Card className="overflow-visible">
        <div className="px-5 py-3 flex items-center justify-between border-b border-ink-100">
          <div className="flex items-center gap-3">
            <div className="text-[14px] font-semibold">Disputes</div>
            <div className="flex items-center bg-ink-100 rounded-lg p-0.5 text-[12.5px] font-medium">
              {STATUS_FILTERS.map(s => (
                <button key={s.value} onClick={() => setFilter(s.value)}
                  className={`px-3 py-1 rounded-md ${filter === s.value ? 'bg-white shadow-sm' : 'text-ink-500'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setExportOpen(o => !o)} disabled={exporting !== null}
                className="h-8 px-3 rounded-lg border border-ink-200 bg-white hover:bg-ink-50 font-semibold text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60">
                <Icon name="download" className="w-3.5 h-3.5" />
                {exporting ? 'Building…' : 'Export Excel'}
              </button>
              {exportOpen && (
                // transparent backdrop: a click anywhere outside closes the menu
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
              )}
              {exportOpen && (
                <div className="absolute right-0 top-full mt-1 z-20 w-72 bg-white border border-ink-200 rounded-lg shadow-lg py-1 text-[12.5px]">
                  <div className="px-3 py-1.5 text-[11px] text-ink-400">
                    Exports the {STATUS_FILTERS.find(s => s.value === filter)?.label ?? filter} tab, all carriers
                  </div>
                  <div className="px-3 pb-2 flex gap-1">
                    {([['summary', 'Summarized'], ['full', 'Extended']] as const).map(([v, label]) => (
                      <button key={v} onClick={() => setExportDetail(v)}
                        className={`flex-1 h-7 rounded-md text-[11.5px] font-semibold border ${
                          exportDetail === v ? 'bg-ink-900 text-white border-ink-900' : 'border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="px-3 pb-1 text-[11px] text-ink-400">
                    {exportDetail === 'summary'
                      ? 'Only the columns needed to file: no status/dates, service, zones, scan sources or addresses.'
                      : 'Every column.'}
                  </div>
                  <button onClick={() => exportExcel('carrier')}
                    className="w-full text-left px-3 py-2 hover:bg-ink-50">
                    <div className="font-semibold">For carrier filing</div>
                    <div className="text-ink-500 text-[11.5px]">Measurements, charges and dispute reason. No customer, rebill or profit data.</div>
                  </button>
                  {canInternal && (
                    <button onClick={() => exportExcel('internal')}
                      className="w-full text-left px-3 py-2 hover:bg-ink-50">
                      <div className="font-semibold text-flame-700">Internal (all data)</div>
                      <div className="text-ink-500 text-[11.5px]">Adds customer, held rebill, ByeLabel cost and profit. Do not send to carriers.</div>
                    </button>
                  )}
                </div>
              )}
            </div>
            {canResolve && pickedNow.length > 0 && (
              <button onClick={() => setSendIds(pickedNow)}
                className="h-8 px-3 rounded-lg bg-royal-600 hover:bg-royal-700 text-white font-semibold text-[12.5px] inline-flex items-center gap-1.5">
                <Icon name="mail" className="w-3.5 h-3.5" /> Send to carrier ({pickedNow.length})…
              </button>
            )}
            {canResolve && pickedNow.length > 0 && !bulkSubmit && (
              <button onClick={() => { setBulkSubmit(true); setWithdrawMsg(null) }}
                className="h-8 px-3 rounded-lg border border-royal-500 text-royal-600 bg-white hover:bg-royal-50 font-semibold text-[12.5px] inline-flex items-center gap-1.5">
                <Icon name="send" className="w-3.5 h-3.5" /> Mark as submitted ({pickedNow.length})…
              </button>
            )}
            {/* "Don't dispute" for the ticked drafts - two clicks, the second says what it does */}
            {canResolve && pickedNow.length > 0 && !confirmWithdrawAll && (
              <button onClick={() => { setConfirmWithdrawAll(true); setWithdrawMsg(null) }}
                className="h-8 px-3 rounded-lg border border-ink-300 bg-white hover:bg-ink-50 font-semibold text-[12.5px]">
                Don't dispute ({pickedNow.length})…
              </button>
            )}
            {canResolve && pickedNow.length > 0 && confirmWithdrawAll && (
              <>
                <button onClick={withdrawPicked} disabled={withdrawing}
                  className="h-8 px-3 rounded-lg bg-ink-800 hover:bg-ink-700 text-white font-semibold text-[12.5px] disabled:opacity-50">
                  {withdrawing ? 'Closing…'
                    : `Confirm: close ${pickedNow.length} as not disputed`
                      + (releaseCount > 0 ? ` · bill ${releaseCount} customer charge${releaseCount === 1 ? '' : 's'} ${fmtMoney(releaseMinor, 'USD')}` : '')}
                </button>
                <button onClick={() => setConfirmWithdrawAll(false)} className="h-8 px-2 text-[11.5px] text-ink-400 hover:text-ink-700">Cancel</button>
              </>
            )}
            {canResolve && (
              <button onClick={() => setShowCreate(true)}
                className="h-8 px-3 rounded-lg bg-flame-500 hover:bg-flame-600 text-white font-semibold text-[12.5px] inline-flex items-center gap-1.5">
                <Icon name="plus" className="w-3.5 h-3.5" /> New dispute
              </button>
            )}
          </div>
        </div>

        {canResolve && bulkSubmit && pickedNow.length > 0 && (
          <div className="px-5 py-3 border-b border-ink-100 bg-royal-50 flex flex-wrap items-end gap-2 text-[12.5px]">
            <div className="w-full text-ink-700">
              Mark <b>{pickedNow.length}</b> ticked draft{pickedNow.length === 1 ? '' : 's'} as submitted - for disputes you filed
              together on {pickedCarriers.join(' / ')}'s website or by phone. Nothing is sent.
              {pickedCarriers.length > 1 && <span className="text-honey-600"> These are for more than one carrier - check that one case number fits all.</span>}
            </div>
            <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Filed via<br />
              <select value={bulkVia} onChange={e => setBulkVia(e.target.value as typeof bulkVia)}
                      className="h-8 px-2 text-[12.5px] border border-ink-200 rounded-md bg-white">
                <option value="portal">Carrier portal</option>
                <option value="phone">Phone</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Carrier case / reference no.<br />
              <input value={bulkCase} onChange={e => setBulkCase(e.target.value)} maxLength={100} placeholder="e.g. 92073387"
                     className="h-8 w-48 px-2 text-[12.5px] border border-ink-200 rounded-md bg-white" />
            </label>
            <button onClick={markPickedSubmitted} disabled={bulkSaving}
              className="h-8 px-3 rounded-lg bg-royal-600 text-white font-semibold text-[12.5px] disabled:opacity-50">
              {bulkSaving ? 'Saving…' : `Confirm: ${pickedNow.length} submitted`}
            </button>
            <button onClick={() => setBulkSubmit(false)} className="h-8 px-2 text-[11.5px] text-ink-500">Cancel</button>
          </div>
        )}

        {/* the big search box */}
        <div className="px-5 py-3 border-b border-ink-100">
          <div className="relative">
            <Icon name="search" className="w-4 h-4 text-ink-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input value={bigQ} onChange={e => setBigQ(e.target.value)}
              placeholder="Search disputes: tracking number, carrier invoice or carrier case number"
              className="w-full h-11 pl-10 pr-24 text-[14px] border border-ink-200 rounded-lg bg-white focus:outline-none focus:border-royal-500" />
            {bigQ && (
              <button onClick={() => setBigQ('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-ink-500 hover:text-ink-800">Clear</button>
            )}
          </div>
          {bigQ && filter !== 'ALL' && (
            <div className="mt-1.5 text-[11.5px] text-ink-500">
              Searching the <b>{STATUS_FILTERS.find(s => s.value === filter)?.label ?? filter}</b> tab only.{' '}
              <button onClick={() => setFilter('ALL')} className="text-royal-600 hover:underline">Search all disputes</button>
            </div>
          )}
        </div>

        {withdrawMsg && (
          <div className={`px-5 py-2 border-b border-ink-100 text-[12.5px] flex items-center gap-3 ${withdrawMsg.ok ? 'bg-mint-50 text-mint-600' : 'bg-flame-50 text-flame-600'}`}>
            <span className="flex-1">{withdrawMsg.text}</span>
            <button onClick={() => setWithdrawMsg(null)} className="text-ink-400 hover:text-ink-700">×</button>
          </div>
        )}
        {/* rule 7: weekly batch; rule 9: categories carriers keep upholding */}
        {canResolve && draftCount > 0 && (
          <div className={`px-5 py-2 border-b border-ink-100 flex flex-wrap items-center gap-3 text-[12.5px] ${sendDay ? 'bg-royal-50' : ''}`}>
            <span>
              <b>Weekly batch:</b> {draftCount} draft dispute{draftCount === 1 ? '' : 's'} waiting.{' '}
              {sendDay ? 'Today is send day - e-mail each carrier its drafts.' : 'Send day is Monday.'}
            </span>
            <button onClick={() => { setDraftF(EMPTY_LIST_FILTER); setAppliedF(EMPTY_LIST_FILTER); setFilter('DRAFT'); setSelectDraftsWhenLoaded(true) }}
              className="h-7 px-3 rounded-md border border-ink-200 bg-white font-semibold hover:bg-ink-50">
              Select all drafts
            </button>
          </div>
        )}
        {losing.length > 0 && (
          <div className="px-5 py-2 border-b border-ink-100 bg-flame-50 text-[12.5px] text-flame-600">
            <b>Carriers keep upholding:</b>{' '}
            {losing.map(c => `${c.category} (${c.won} of ${c.decided} won)`).join(', ')}. Consider billing these instead of disputing.
          </div>
        )}

        {/* filters (Sahin 2026-10-22) */}
        <div className="px-5 py-3 border-b border-ink-100 flex flex-wrap items-end gap-2 text-[12px]">
          <FilterField label="Carrier">
            <select value={draftF.carrier} onChange={e => setDraftF(p => ({ ...p, carrier: e.target.value }))} className="h-8 px-2 border border-ink-200 rounded-md bg-white">
              <option value="">All</option>
              {['APC', 'ASENDIA', 'CANADAPOST', 'DHL', 'EPOST', 'EVRI', 'FEDEX', 'INTELCOM', 'UNIUNI', 'UPS', 'USPS'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FilterField>
          <FilterField label="Category">
            <select value={draftF.category} onChange={e => setDraftF(p => ({ ...p, category: e.target.value }))} className="h-8 px-2 border border-ink-200 rounded-md bg-white">
              <option value="">All</option>
              {(list?.facets?.categories ?? []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FilterField>
          <FilterField label="Customer">
            <select value={draftF.customer} onChange={e => setDraftF(p => ({ ...p, customer: e.target.value }))} className="h-8 px-2 border border-ink-200 rounded-md bg-white max-w-[220px]">
              <option value="">All</option>
              {(list?.facets?.customers ?? []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FilterField>
          <FilterField label="At stake ($)">
            <div className="flex items-center gap-1">
              <input type="number" min={0} step="0.01" value={draftF.amount_min} placeholder="min" aria-label="Minimum amount"
                onChange={e => setDraftF(p => ({ ...p, amount_min: e.target.value }))} onKeyDown={e => e.key === 'Enter' && applyF()}
                className="h-8 px-2 border border-ink-200 rounded-md w-20" />
              <span className="text-ink-400">to</span>
              <input type="number" min={0} step="0.01" value={draftF.amount_max} placeholder="max" aria-label="Maximum amount"
                onChange={e => setDraftF(p => ({ ...p, amount_max: e.target.value }))} onKeyDown={e => e.key === 'Enter' && applyF()}
                className="h-8 px-2 border border-ink-200 rounded-md w-20" />
            </div>
          </FilterField>
          <FilterField label="Age (days)">
            <div className="flex items-center gap-1">
              <input type="number" min={0} value={draftF.age_min} placeholder="min" aria-label="Minimum age in days"
                onChange={e => setDraftF(p => ({ ...p, age_min: e.target.value }))} onKeyDown={e => e.key === 'Enter' && applyF()}
                className="h-8 px-2 border border-ink-200 rounded-md w-16" />
              <span className="text-ink-400">to</span>
              <input type="number" min={0} value={draftF.age_max} placeholder="max" aria-label="Maximum age in days"
                onChange={e => setDraftF(p => ({ ...p, age_max: e.target.value }))} onKeyDown={e => e.key === 'Enter' && applyF()}
                className="h-8 px-2 border border-ink-200 rounded-md w-16" />
            </div>
          </FilterField>
          <FilterField label="Shipped">
            <div className="flex items-center gap-1">
              <input type="date" value={draftF.ship_from} aria-label="Shipped from"
                onChange={e => setDraftF(p => ({ ...p, ship_from: e.target.value }))}
                className="h-8 px-2 border border-ink-200 rounded-md" />
              <span className="text-ink-400">to</span>
              <input type="date" value={draftF.ship_to} aria-label="Shipped to"
                onChange={e => setDraftF(p => ({ ...p, ship_to: e.target.value }))}
                className="h-8 px-2 border border-ink-200 rounded-md" />
            </div>
          </FilterField>
          <FilterField label="Follow-up">
            <label className="h-8 flex items-center gap-1.5 px-2 border border-ink-200 rounded-md bg-white cursor-pointer">
              <input type="checkbox" checked={draftF.follow_up === '1'}
                onChange={e => setDraftF(p => ({ ...p, follow_up: e.target.checked ? '1' : '' }))} />
              {FOLLOW_UP_DAYS}+ days, no answer
            </label>
          </FilterField>
          <FilterField label="Carrier e-mail">
            <label className="h-8 flex items-center gap-1.5 px-2 border border-ink-200 rounded-md bg-white cursor-pointer">
              <input type="checkbox" checked={draftF.replied === '1'}
                onChange={e => setDraftF(p => ({ ...p, replied: e.target.checked ? '1' : '' }))} />
              carrier replied, to review
            </label>
          </FilterField>
          <FilterField label="Carrier credit">
            <label className="h-8 flex items-center gap-1.5 px-2 border border-ink-200 rounded-md bg-white cursor-pointer">
              <input type="checkbox" checked={draftF.credit === '1'}
                onChange={e => setDraftF(p => ({ ...p, credit: e.target.checked ? '1' : '' }))} />
              credit found, to review
            </label>
          </FilterField>
          <FilterField label="Deadline">
            <select value={draftF.deadline} onChange={e => setDraftF(p => ({ ...p, deadline: e.target.value as ListFilter['deadline'] }))}
              className="h-8 px-2 border border-ink-200 rounded-md bg-white">
              <option value="">All</option>
              <option value="soon">Due within {DEADLINE_WARN_DAYS} days</option>
              <option value="passed">Deadline passed</option>
            </select>
          </FilterField>
          <button onClick={clearF} className="h-8 px-3 border border-ink-200 rounded-md text-ink-500 hover:bg-ink-50">Clear</button>
          <button onClick={applyF} className="h-8 px-4 bg-ink-800 text-white rounded-md font-medium hover:bg-ink-700">Apply</button>
          <div className="ml-auto text-ink-500 self-center">
            {list?.total != null && <>{list.total} dispute{list.total === 1 ? '' : 's'} · <b className="text-ink-800">{fmtMoney(list.total_minor ?? 0, currency)}</b> at stake{anyF ? ' (filtered)' : ''}</>}
            {list?.total != null && list.total > (list.disputes?.length ?? 0) && <span className="text-flame-600"> · showing first {list.disputes.length}</span>}
          </div>
        </div>

        <table className="w-full text-[13px]">
          <thead className="bg-ink-50 text-ink-500 text-[10.5px] uppercase tracking-wider">
            <tr>
              <th className="w-8 pl-4">
                {canResolve && drafts.length > 0 && (
                  <input type="checkbox" checked={allPicked} title="Select all draft disputes"
                    onChange={() => setPicked(allPicked ? new Set() : new Set(drafts.map(d => d.dispute_id)))} />
                )}
              </th>
              <th className="text-left font-semibold py-2 px-5">Dispute</th>
              <th className="text-left font-semibold py-2">Detected</th>
              <th className="text-left font-semibold py-2">Shipped</th>
              <th className="text-left font-semibold py-2">Invoice date</th>
              <th className="text-left font-semibold py-2">Carrier</th>
              <th className="text-left font-semibold py-2">Shipment</th>
              <th className="text-left font-semibold py-2">Category</th>
              <th className="text-right font-semibold py-2">At stake</th>
              <th className="text-left font-semibold py-2 pl-4">Age</th>
              <th className="text-left font-semibold py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={11} className="py-10 text-center text-ink-500">Loading…</td></tr>}
            {!isLoading && list?.disputes.length === 0 && (
              <tr><td colSpan={11} className="py-10 text-center text-ink-500">
                No disputes in this view.
              </td></tr>
            )}
            {list?.disputes.map(d => {
              const age = d.status === 'DRAFT' ? daysSince(d.detected_at)
                : RESOLVED.includes(d.status) ? null
                : daysSince(d.submitted_at)
              const ageText = RESOLVED.includes(d.status)
                ? fmtDate(d.resolved_at)
                : age == null ? '—' : `${age}d`
              const stale = d.status === 'SUBMITTED' && age != null && age >= FOLLOW_UP_DAYS
              const escalate = stale && age != null && age >= ESCALATE_DAYS
              const small = d.status === 'DRAFT' && d.at_stake_minor < SMALL_DISPUTE_MINOR
              return (
                <tr key={d.id}
                  onClick={e => {
                    if (e.shiftKey && canResolve && d.status === 'DRAFT') { togglePick(d.dispute_id, true); return }
                    setDetailId(d.dispute_id)
                  }}
                  onMouseDown={e => { if (e.shiftKey) e.preventDefault() /* no text highlight on shift-click */ }}
                  className={`border-t border-ink-100 cursor-pointer hover:bg-ink-50 ${detailId === d.dispute_id ? 'bg-royal-50' : ''}`}>
                  <td className="pl-4" onClick={e => e.stopPropagation()}>
                    {canResolve && d.status === 'DRAFT' && (
                      <input type="checkbox" checked={picked.has(d.dispute_id)} onChange={() => { /* handled by onClick (needs shiftKey) */ }}
                        onClick={e => togglePick(d.dispute_id, e.shiftKey)}
                        onMouseDown={e => { if (e.shiftKey) e.preventDefault() /* no text highlight */ }}
                        title="Shift-click to select a range" />
                    )}
                  </td>
                  <td className="py-2.5 px-5 font-semibold mono text-[12.5px]">{d.id}</td>
                  <td className="text-ink-600 num">{fmtDate(d.detected_at)}</td>
                  <td className="text-ink-600 num">{fmtDate(d.ship_date ?? null)}</td>
                  <td className="text-ink-600 num">
                    {fmtDate(d.invoice_date ?? null)}
                    {d.status === 'DRAFT' && d.deadline && d.days_left != null && (
                      <div className={`text-[10.5px] ${d.days_left < 0 ? 'text-flame-600 font-semibold' : d.days_left <= DEADLINE_WARN_DAYS ? 'text-honey-600 font-semibold' : 'text-ink-400'}`}
                        title="Last day to file this dispute with the carrier (Carrier Recovery → Settings → carrier rules)">
                        {d.days_left < 0 ? `deadline passed ${fmtDate(d.deadline)}` : `file by ${fmtDate(d.deadline)} · ${d.days_left}d`}
                      </div>
                    )}
                  </td>
                  <td><span className="pill pill-ink">{d.carrier.toUpperCase()}</span></td>
                  <td className="mono text-[12px] text-ink-600 max-w-[180px] truncate" title={d.tracking}>{d.tracking}</td>
                  <td className="text-ink-700 text-[12.5px]">
                    {d.type}
                    {losingCats.has(d.type) && <span className="ml-1 pill pill-flame text-[10px]" title="Carriers upheld most disputes in this category">losing</span>}
                    {d.handling_case === 'under' && (
                      <span className="ml-1 pill pill-mint text-[10px]"
                        title={`The declared box is under the carrier's handling-fee limits${d.rules_confirmed ? '' : ' (limits not confirmed yet)'}`}>
                        strong case{d.rules_confirmed ? '' : '?'}
                      </span>
                    )}
                    {d.handling_case === 'over' && (
                      <span className="ml-1 pill pill-ink text-[10px]"
                        title={`The declared box is over the carrier's handling-fee limits, so the fee is likely correct${d.rules_confirmed ? '' : ' (limits not confirmed yet)'}`}>
                        over limits
                      </span>
                    )}
                    {(d.replies_new ?? 0) > 0 && (
                      <span className="ml-1 pill pill-royal text-[10px]"
                        title="The carrier e-mailed about this dispute - open it to read and review">
                        carrier replied{(d.replies_new ?? 0) > 1 ? ` ×${d.replies_new}` : ''}
                      </span>
                    )}
                    {(d.credits_new ?? 0) > 0 && (
                      <span className="ml-1 pill pill-mint text-[10px]"
                        title="The carrier credited this parcel in a later invoice or credit memo - open it to review">
                        credit found {fmtMoney(d.credits_new_minor ?? 0, currency)}
                      </span>
                    )}
                    {(d.customer_upheld ?? 0) > 0 && (
                      <span className="ml-1 pill pill-honey text-[10px]"
                        title={`The carrier upheld ${d.customer_upheld} earlier dispute(s) of this customer - consider billing instead`}>
                        upheld before ×{d.customer_upheld}
                      </span>
                    )}
                  </td>
                  <td className="text-right num font-semibold">
                    {fmtMoney(d.at_stake_minor, currency)}
                    {small && <div className="text-[10px] font-normal text-ink-400" title="Under $3: usually not worth a dispute">under $3</div>}
                  </td>
                  <td className={`pl-4 num ${stale ? 'text-flame-600 font-semibold' : 'text-ink-500'}`}>
                    {ageText}
                    {escalate ? <span className="ml-1 pill pill-flame text-[10px]">escalate</span>
                      : stale ? <span className="ml-1 pill pill-honey text-[10px]">follow up</span> : null}
                  </td>
                  <td><span className={`pill ${statusPill(d.status)}`}>{statusLabel(d.status)}</span></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>

      {selected && (
        <DisputeDrawer key={selected.dispute_id} dispute={selected} canResolve={canResolve}
          onClose={() => setDetailId(null)} onSendEmail={ids => setSendIds(ids)} />
      )}
      {showCreate && <NewDisputeModal onClose={() => setShowCreate(false)} />}
      {sendIds && (
        <SendToCarrierModal ids={sendIds} onClose={() => setSendIds(null)}
          onSent={() => { setPicked(new Set()); setDetailId(null) }} />
      )}
    </div>
  )
}

// ============================================================
// Right-side detail drawer — the whole flow lives here
// ============================================================
const VIA_LABEL: Record<string, string> = { email: 'e-mailed from finops', portal: 'carrier portal', phone: 'phone', other: 'other' }

// The carrier's case / reference number of a submitted dispute (migration 079):
// how carrier e-mails are matched to it. Can be added or corrected later.
function CaseRefEditor({ d, canEdit, onSaved }: { d: Dispute; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(d.case_ref ?? '')
  const [saved, setSaved] = useState(d.case_ref ?? '')
  const [err, setErr] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/disputes/${d.dispute_id}/case`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_ref: val }),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Saving failed')
    },
    onSuccess: () => { setSaved(val.trim()); setEditing(false); setErr(null); onSaved() },
    onError: (e: Error) => setErr(e.message),
  })
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        <input value={val} onChange={e => setVal(e.target.value)} maxLength={100} autoFocus
               className="h-7 w-40 px-2 text-[12px] border border-ink-200 rounded-md" />
        <button onClick={() => save.mutate()} disabled={save.isPending} className="text-[11.5px] text-royal-600 font-medium">Save</button>
        <button onClick={() => { setVal(saved); setEditing(false) }} className="text-[11.5px] text-ink-500">Cancel</button>
        {err && <span className="text-[11px] text-flame-600">{err}</span>}
      </span>
    )
  }
  return (
    <span>
      {saved ? <span className="mono">{saved}</span> : <span className="text-ink-400">none</span>}
      {d.submitted_via && <span className="text-ink-400"> · {VIA_LABEL[d.submitted_via] ?? d.submitted_via}</span>}
      {canEdit && <button onClick={() => setEditing(true)} className="ml-2 text-[11px] text-royal-600 hover:underline">{saved ? 'edit' : 'add'}</button>}
    </span>
  )
}

// Carrier credits found for this dispute (match_dispute_credits.py, nightly).
// Apply = record the carrier's decision through /resolve (credit covers the
// disputed amount -> carrier waived, else a partial credit of the total found);
// two clicks, like every decision. Dismiss = "this credit is about something else".
interface FoundCredit {
  id: number; source_table: string; carrier_ref: string | null; credit_date: string | null
  amount: string; description: string | null; status: 'NEW' | 'APPLIED' | 'DISMISSED'; decided_by: string | null
  also_on: number[]   // the same credit line was matched to these other open disputes of the parcel
}
const CREDIT_SOURCE: Record<string, string> = {
  usps_transaction_details: 'USPS refund', ups_invoices: 'UPS invoice', dhl_invoices: 'DHL invoice',
  asendia_credit_lines: 'Asendia credit memo', epost_credit_lines: 'ePost credit memo',
  carrier_payment_allocations: 'Carrier payment',
}

function DisputeCredits({ d, canResolve, onDone }: { d: Dispute; canResolve: boolean; onDone: () => void }) {
  const { currency } = useCurrency()
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const { data } = useQuery<{ credits: FoundCredit[] }>({
    queryKey: ['dispute-credits', d.dispute_id],
    queryFn: () => fetch(`/api/disputes/${d.dispute_id}/credits`).then(r => r.json()),
  })
  const credits = data?.credits ?? []
  const open = credits.filter(c => c.status === 'NEW')
  const totalMinor = open.reduce((s, c) => s + Math.round(Number(c.amount) * 100), 0)
  const full = totalMinor >= d.at_stake_minor - 1
  const decided = d.status !== 'DRAFT' && d.status !== 'SUBMITTED'

  const apply = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/disputes/${d.dispute_id}/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(full ? { outcome: 'WAIVED' } : { outcome: 'PARTIAL', credit_amount: totalMinor / 100 }),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Applying the credit failed')
    },
    onSuccess: onDone,
    onError: (e: Error) => { setErr(e.message); setConfirm(false) },
  })
  const dismiss = useMutation({
    mutationFn: async (cid: number) => {
      const r = await fetch(`/api/disputes/${d.dispute_id}/credits/${cid}/dismiss`, { method: 'POST' })
      if (!r.ok) throw new Error((await r.json())?.error || 'Dismiss failed')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispute-credits', d.dispute_id] })
      qc.invalidateQueries({ queryKey: ['disputes'] })
    },
    onError: (e: Error) => setErr(e.message),
  })

  if (!credits.length) return null
  return (
    <div>
      <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Carrier credits found</div>
      <ul className="space-y-1.5">
        {credits.map(c => (
          <li key={c.id} className={`text-[12px] rounded-md px-2.5 py-1.5 ${c.status === 'NEW' ? 'bg-mint-50' : 'bg-ink-50 text-ink-500'}`}>
            <div className="flex items-center justify-between gap-2">
              <span>
                <span className="font-semibold">{fmtMoney(Math.round(Number(c.amount) * 100), currency)}</span>
                {' · '}{CREDIT_SOURCE[c.source_table] ?? c.source_table} {c.carrier_ref}
                {c.credit_date && <> · {c.credit_date}</>}
              </span>
              {c.status === 'NEW' && canResolve && !decided && (
                <button onClick={() => dismiss.mutate(c.id)} disabled={dismiss.isPending}
                        className="text-[11px] text-ink-500 hover:text-ink-800 shrink-0" title="This credit is about something else">
                  Not related
                </button>
              )}
              {c.status !== 'NEW' && <span className="text-[10.5px] shrink-0">{c.status === 'APPLIED' ? 'applied' : 'not related'}{c.decided_by ? ` · ${c.decided_by}` : ''}</span>}
            </div>
            {c.description && <div className="text-[11px] text-ink-500">{c.description}</div>}
            {c.status === 'NEW' && c.also_on?.length > 0 && (
              <div className="text-[11px] text-honey-600">
                Also matched to dispute{c.also_on.length > 1 ? 's' : ''} {c.also_on.map(x => `#${x}`).join(', ')} (same parcel) -
                check which charge it credits; applying it here takes it off the others.
              </div>
            )}
          </li>
        ))}
      </ul>
      {open.length > 0 && canResolve && !decided && (
        <div className="mt-2">
          {!confirm ? (
            <button onClick={() => { setErr(null); setConfirm(true) }}
                    className="h-8 px-3 rounded-md text-[12px] font-medium bg-mint-500 text-white hover:bg-mint-600">
              {full ? `Apply: carrier credited in full (${fmtMoney(totalMinor, currency)})`
                    : `Apply: partial credit ${fmtMoney(totalMinor, currency)} of ${fmtMoney(d.at_stake_minor, currency)}`}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => apply.mutate()} disabled={apply.isPending}
                      className="h-8 px-3 rounded-md text-[12px] font-semibold bg-mint-600 text-white">
                {apply.isPending ? 'Applying…' : 'Confirm'}
              </button>
              <button onClick={() => setConfirm(false)} className="text-[12px] text-ink-500">Cancel</button>
              <span className="text-[11px] text-ink-500">
                {full ? 'Dispute -> won; the held rebill is voided (customer not billed).'
                      : 'Dispute -> credited; the held rebill is reduced by the credit.'}
              </span>
            </div>
          )}
          {err && <p className="text-[11px] text-flame-600 mt-1">{err}</p>}
        </div>
      )}
    </div>
  )
}

function DisputeDrawer({ dispute: d, canResolve, onClose, onSendEmail }:
  { dispute: Dispute; canResolve: boolean; onClose: () => void; onSendEmail: (ids: number[]) => void }) {
  const { currency } = useCurrency()
  const qc = useQueryClient()
  const [outcome, setOutcome] = useState<'WAIVED' | 'UPHELD' | 'PARTIAL' | null>(null)
  const [creditAmt, setCreditAmt] = useState('')
  const [confirmWithdraw, setConfirmWithdraw] = useState(false)
  const [notes, setNotes] = useState(d.notes ?? '')
  const [notesSaved, setNotesSaved] = useState(d.notes ?? '')
  const { token } = useAuth()

  // The linked rebill's evidence (what the carrier charged / measured) — the
  // same endpoint and panel as the Carrier Adjustments drawer.
  const evidenceQ = useQuery<{ evidence_json?: unknown; carrier?: string; rebill_reason?: string | null; error?: string }>({
    queryKey: ['dispute-rebill-evidence', d.rebill_id],
    queryFn: () => fetch(`/api/byelabel-sync/rebill/${d.rebill_id}`, { headers: authHeaders(token) }).then(r => r.json()),
    enabled: d.rebill_id != null,
  })
  const [err, setErr] = useState<string | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['disputes'] })
    qc.invalidateQueries({ queryKey: ['disputes-summary'] })
  }

  // Mark as submitted asks how it was filed and the carrier's case number (migration 079)
  const [submitForm, setSubmitForm] = useState(false)
  const [via, setVia] = useState<'portal' | 'phone' | 'other'>('portal')
  const [caseRef, setCaseRef] = useState('')
  const markSubmitted = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/disputes/${d.dispute_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'SUBMITTED', via, case_ref: caseRef }),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Update failed')
      return r.json()
    },
    onSuccess: () => { invalidate(); onClose() },
    onError: (e: Error) => setErr(e.message),
  })

  const saveNotes = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/disputes/${d.dispute_id}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Saving the notes failed')
      return r.json() as Promise<{ notes: string | null }>
    },
    onSuccess: (res) => { setErr(null); setNotes(res.notes ?? ''); setNotesSaved(res.notes ?? ''); invalidate() },
    onError: (e: Error) => setErr(e.message),
  })

  const resolve = useMutation({
    mutationFn: async (a: { outcome: string; credit_amount?: number }) => {
      const r = await fetch(`/api/disputes/${d.dispute_id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(a),
      })
      if (!r.ok) throw new Error((await r.json())?.error || 'Resolve failed')
      return r.json()
    },
    onSuccess: () => { invalidate(); onClose() },
    onError: (e: Error) => { setErr(e.message); setConfirmWithdraw(false) },
  })

  const isResolved = RESOLVED.includes(d.status)
  // Step index the dispute is AT: 0 detected, 1 submitted, 2 decided.
  const step = isResolved ? 2 : d.status === 'SUBMITTED' ? 1 : 0

  function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
      <div className="flex items-start gap-2 py-1.5 border-b border-ink-50 last:border-0">
        <span className="text-[11px] font-medium text-ink-400 w-28 shrink-0 pt-0.5">{label}</span>
        <span className="text-[12.5px] text-ink-800 break-words">{value ?? '—'}</span>
      </div>
    )
  }

  const steps = [
    { name: 'Detected', date: d.detected_at, by: d.detected_by },
    { name: 'Submitted to carrier', date: d.submitted_at, by: d.submitted_by },
    { name: 'Carrier decision', date: d.resolved_at, by: d.resolved_by },
  ]

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-[460px] z-50 bg-white border-l border-ink-200 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 shrink-0">
          <div>
            <div className="text-[14px] font-semibold text-ink-900 mono">{d.id}</div>
            <div className="text-[11.5px] text-ink-500">{d.carrier.toUpperCase()} · {d.type}</div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* progress tracker */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-3">Progress</div>
            <div className="space-y-0">
              {steps.map((s, i) => {
                const done = i < step || (i === step && isResolved)
                const current = i === step && !isResolved
                return (
                  <div key={s.name} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-4 h-4 rounded-full border-2 grid place-items-center ${
                        done ? 'bg-mint-500 border-mint-500'
                        : current ? 'bg-white border-royal-500'
                        : 'bg-white border-ink-200'}`}>
                        {done && <Icon name="check" className="w-2.5 h-2.5 text-white" />}
                      </div>
                      {i < 2 && <div className={`w-0.5 flex-1 min-h-[22px] ${i < step ? 'bg-mint-500' : 'bg-ink-150'}`} />}
                    </div>
                    <div className="pb-3">
                      <div className={`text-[12.5px] ${current ? 'font-semibold text-royal-700' : done ? 'text-ink-800' : 'text-ink-400'}`}>
                        {i === 2 && isResolved ? `Carrier decision — ${statusLabel(d.status)}` : s.name}
                      </div>
                      <div className="text-[11px] text-ink-400">
                        {s.date ? fmtDate(s.date) : (current ? 'now' : 'pending')}
                        {s.date && (s.by
                          ? <> · by <span className="text-ink-600">{s.by}</span></>
                          : i > 0 && <> · by <span className="italic">not recorded</span></>)}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* dispute detail */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Dispute</div>
            <Row label="Shipment" value={<span className="mono text-[12px]">{d.tracking}</span>} />
            <Row label="Category" value={d.type} />
            <Row label="At stake" value={<span className="font-semibold">{fmtMoney(d.at_stake_minor, currency)}</span>} />
            <Row label="Reason" value={d.detail} />
            {d.status !== 'DRAFT' && (
              <Row label="Carrier case" value={<CaseRefEditor d={d} canEdit={canResolve} onSaved={invalidate} />} />
            )}
          </div>

          {/* our notes - internal, never in the carrier file or e-mail */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">
              Notes <span className="normal-case font-normal">· internal, never sent to the carrier</span>
            </div>
            {canResolve ? (
              <>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={4000}
                          placeholder="e.g. called the rep on 10/24, waiting for the case number"
                          className="w-full text-[12.5px] border border-ink-200 rounded-md p-2 focus:outline-none focus:border-royal-500" />
                <div className="flex items-center gap-2 mt-1.5">
                  <button onClick={() => saveNotes.mutate()}
                          disabled={saveNotes.isPending || notes.trim() === notesSaved.trim()}
                          className="h-7 px-3 rounded-md text-[12px] font-medium bg-royal-500 text-white hover:bg-royal-600 disabled:opacity-40">
                    {saveNotes.isPending ? 'Saving…' : 'Save notes'}
                  </button>
                  {notes.trim() !== notesSaved.trim() && (
                    <button onClick={() => setNotes(notesSaved)} className="text-[12px] text-ink-500 hover:text-ink-800">Undo changes</button>
                  )}
                  {saveNotes.isSuccess && notes.trim() === notesSaved.trim() && (
                    <span className="text-[11px] text-mint-600">Saved</span>
                  )}
                </div>
              </>
            ) : (
              <p className="text-[12px] text-ink-700 whitespace-pre-wrap bg-ink-50 rounded-md p-2">{d.notes || 'No notes.'}</p>
            )}
          </div>

          <DisputeReplies disputeId={d.dispute_id} />

          <DisputeCredits d={d} canResolve={canResolve} onDone={() => { invalidate(); onClose() }} />

          <RecordHistory entity="carrier_disputes" id={d.dispute_id} />

          {/* evidence: what was declared vs what the carrier charged / measured */}
          <div className="space-y-4">
            {d.rebill_id == null ? (
              <div>
                <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Evidence</div>
                <p className="text-[11px] text-ink-500 bg-ink-25 rounded-md p-2">No rebill evidence linked to this dispute.</p>
              </div>
            ) : evidenceQ.isLoading ? (
              <p className="text-[12px] text-ink-400">Loading evidence…</p>
            ) : evidenceQ.data && !evidenceQ.data.error ? (
              <RebillEvidence evidence={evidenceQ.data.evidence_json} carrier={evidenceQ.data.carrier ?? d.carrier}
                              reason={evidenceQ.data.rebill_reason} />
            ) : (
              <p className="text-[11px] text-flame-600">Evidence could not be loaded.</p>
            )}
          </div>

          {/* the money it controls */}
          {d.rebill_id != null && (
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Held rebill</div>
              <Row label="Customer" value={d.rebill_customer} />
              <Row label="Rebill" value={d.rebill_amount_minor != null
                ? <span className="font-semibold text-mint-700">{fmtMoney(d.rebill_amount_minor, currency)}</span> : '—'} />
              <Row label="Rebill status" value={d.rebill_status
                ? <span className={`pill ${statusPill(d.rebill_status)}`}>{d.rebill_status}</span> : '—'} />
              {!isResolved && (
                <div className="mt-2 text-[11.5px] text-ink-500 leading-relaxed bg-ink-25 rounded-lg p-2.5">
                  Waived → this rebill is <b className="text-flame-600">voided</b> (customer not billed).<br />
                  Upheld / Don't dispute → customer is <b className="text-mint-700">billed {d.rebill_amount_minor != null ? fmtMoney(d.rebill_amount_minor, currency) : 'in full'}</b>.<br />
                  Partial → reduced to the upheld amount, then billed.
                </div>
              )}
            </div>
          )}

          {/* the action for where this dispute is */}
          {!canResolve && !isResolved && (
            <div className="text-[12px] text-ink-400">You need the Resolve disputes permission to action disputes.</div>
          )}

          {canResolve && d.status === 'DRAFT' && (
            <div className="rounded-lg border border-ink-200 p-3">
              <div className="text-[12.5px] font-semibold text-ink-800 mb-1">Next: submit to {d.carrier.toUpperCase()}</div>
              <div className="text-[11.5px] text-ink-500 leading-relaxed mb-3">
                "E-mail" sends the dispute request to {d.carrier.toUpperCase()}'s contact from info@byelabel.com (you see it
                first) and marks it submitted. If you file it in {d.carrier.toUpperCase()}'s own portal instead, use
                "Mark as submitted" - that sends nothing.
              </div>
              {err && <div className="mb-2 text-[12px] text-flame-600">{err}</div>}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onSendEmail([d.dispute_id])}
                  className="h-8 px-3 rounded-md bg-flame-500 hover:bg-flame-600 text-white font-semibold text-[12.5px] inline-flex items-center gap-1.5">
                  <Icon name="mail" className="w-3.5 h-3.5" /> E-mail to {d.carrier.toUpperCase()}…
                </button>
                {!submitForm && (
                  <button
                    onClick={() => setSubmitForm(true)}
                    className="h-8 px-3 rounded-md bg-royal-600 hover:bg-royal-500 text-white font-semibold text-[12.5px] inline-flex items-center gap-1.5">
                    <Icon name="send" className="w-3.5 h-3.5" /> Mark as submitted…
                  </button>
                )}
              </div>
              {submitForm && (
                <div className="mt-3 rounded-md bg-royal-50 p-3 space-y-2">
                  <div className="flex flex-wrap gap-2 items-end">
                    <label className="text-[10.5px] font-semibold text-ink-500 uppercase">Filed via<br />
                      <select value={via} onChange={e => setVia(e.target.value as typeof via)}
                              className="h-8 px-2 text-[12.5px] border border-ink-200 rounded-md bg-white">
                        <option value="portal">{d.carrier.toUpperCase()} portal</option>
                        <option value="phone">Phone</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label className="text-[10.5px] font-semibold text-ink-500 uppercase flex-1 min-w-[160px]">Carrier case / reference no.<br />
                      <input value={caseRef} onChange={e => setCaseRef(e.target.value)} maxLength={100}
                             placeholder="e.g. 120247491"
                             className="h-8 w-full px-2 text-[12.5px] border border-ink-200 rounded-md bg-white" />
                    </label>
                  </div>
                  <div className="text-[11px] text-ink-500">
                    The case number is how the carrier's e-mails about it are matched to this dispute. You can add it later.
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => markSubmitted.mutate()} disabled={markSubmitted.isPending}
                            className="h-8 px-3 rounded-md bg-royal-600 text-white font-semibold text-[12.5px] disabled:opacity-50">
                      {markSubmitted.isPending ? 'Saving…' : 'Confirm: submitted'}
                    </button>
                    <button onClick={() => setSubmitForm(false)} className="text-[12px] text-ink-500">Cancel</button>
                  </div>
                </div>
              )}

              {/* Or: decide not to dispute (nothing goes to the carrier). Two clicks (§3). */}
              <div className="mt-3 pt-3 border-t border-ink-100">
                <div className="text-[11.5px] text-ink-500 leading-relaxed mb-2">
                  Not worth disputing? Close it without contacting {d.carrier.toUpperCase()}: the rebill is released and
                  the customer is billed in full. Recorded as <b>Not disputed</b> — it does not count as a lost dispute.
                </div>
                <button
                  onClick={() => {
                    if (!confirmWithdraw) { setConfirmWithdraw(true); return }
                    resolve.mutate({ outcome: 'WITHDRAW' })
                  }}
                  disabled={resolve.isPending}
                  className={`h-8 px-3 rounded-md font-semibold text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-50 ${
                    confirmWithdraw ? 'bg-ink-800 text-white hover:bg-ink-700' : 'border border-ink-300 text-ink-700 hover:bg-ink-50'}`}>
                  <Icon name="check" className="w-3.5 h-3.5" />
                  {resolve.isPending ? 'Saving…' : confirmWithdraw
                    ? `Confirm — bill ${d.rebill_amount_minor != null ? fmtMoney(d.rebill_amount_minor, currency) : 'in full'}`
                    : "Don't dispute — bill the customer"}
                </button>
                {confirmWithdraw && !resolve.isPending && (
                  <button onClick={() => setConfirmWithdraw(false)} className="ml-2 h-8 px-2 text-[11.5px] text-ink-400 hover:text-ink-700">Cancel</button>
                )}
              </div>
            </div>
          )}

          {canResolve && d.status === 'SUBMITTED' && (
            <div className="rounded-lg border border-ink-200 p-3">
              <div className="text-[12.5px] font-semibold text-ink-800 mb-2">Record {d.carrier.toUpperCase()}'s decision</div>
              <div className="flex flex-col gap-1.5">
                <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                  <input type="radio" name="outcome" checked={outcome === 'WAIVED'} onChange={() => setOutcome('WAIVED')} />
                  Waived (their error) — <span className="text-flame-600">void the rebill</span>
                </label>
                <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                  <input type="radio" name="outcome" checked={outcome === 'UPHELD'} onChange={() => setOutcome('UPHELD')} />
                  Upheld — <span className="text-mint-700">bill in full</span>
                </label>
                <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
                  <input type="radio" name="outcome" checked={outcome === 'PARTIAL'} onChange={() => setOutcome('PARTIAL')} />
                  Partial credit — reduce &amp; bill
                </label>
                {outcome === 'PARTIAL' && (
                  <input type="number" step="0.01" min="0" value={creditAmt}
                    onChange={e => setCreditAmt(e.target.value)}
                    placeholder="Credit from carrier (USD)"
                    className="mt-1 w-full px-2 py-1 text-[12px] border border-ink-200 rounded" autoFocus />
                )}
              </div>
              {err && <div className="mt-2 text-[12px] text-flame-600">{err}</div>}
              <button
                disabled={!outcome || (outcome === 'PARTIAL' && !(Number(creditAmt) > 0)) || resolve.isPending}
                onClick={() => resolve.mutate({
                  outcome: outcome!, credit_amount: outcome === 'PARTIAL' ? Number(creditAmt) : undefined,
                })}
                className="mt-3 h-8 px-3 rounded-md bg-ink-800 text-white font-semibold text-[12.5px] disabled:opacity-40">
                {resolve.isPending ? 'Saving…' : 'Confirm decision'}
              </button>
            </div>
          )}

          {isResolved && (
            <div className="rounded-lg border border-ink-200 p-3 bg-ink-25">
              <div className="text-[12.5px] font-semibold text-ink-800 mb-1">
                Resolved — {statusLabel(d.status)}
              </div>
              <div className="text-[11.5px] text-ink-500 leading-relaxed">
                {d.status === 'ACCEPTED' && 'Carrier waived the charge. The rebill was voided — the customer was not billed.'}
                {d.status === 'REJECTED' && 'Carrier upheld the charge. The rebill was released and billed in full.'}
                {d.status === 'WITHDRAWN' && 'Not disputed — closed without contacting the carrier. The rebill was released and billed in full.'}
                {d.status === 'CREDITED' && `Carrier gave a partial credit of ${fmtMoney(d.credit_minor, currency)}. The rebill was reduced and billed.`}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ============================================================
// "+ New dispute" modal
// ============================================================
// prefill: from the "To review" tab (an overcharge nothing else caught)
export interface DisputePrefill { carrier: string; invoice: string; shipment: string; amount: string; chargeName: string }

export function NewDisputeModal({ onClose, prefill, onCreated }:
  { onClose: () => void; prefill?: DisputePrefill; onCreated?: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [carrier, setCarrier]               = useState(prefill?.carrier ?? 'DHL')
  const [invoice, setInvoice]               = useState(prefill?.invoice ?? '')
  const [shipment, setShipment]             = useState(prefill?.shipment ?? '')
  const [code, setCode]                     = useState('')
  const [chargeName, setChargeName]         = useState(prefill?.chargeName ?? '')
  const [amount, setAmount]                 = useState(prefill?.amount ?? '')
  const [category, setCategory]             = useState('')
  const [reason, setReason]                 = useState('')
  const [submitNow, setSubmitNow]           = useState(false)
  const [errors, setErrors]                 = useState<string | null>(null)

  const createDispute = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/disputes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carrier,
          invoice_number: invoice,
          shipment_number: shipment,
          charge_code: code || null,
          charge_name: chargeName || null,
          disputed_amount: Number(amount),
          dispute_category: category,
          dispute_reason: reason,
          status: submitNow ? 'SUBMITTED' : 'DRAFT',
        }),
      })
      const json = await r.json()
      if (!r.ok || json.ok === false) throw new Error(json.error || 'Create failed')
      return json
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disputes'] })
      qc.invalidateQueries({ queryKey: ['disputes-summary'] })
      onCreated?.()
      onClose()
    },
    onError: (e: Error) => setErrors(e.message),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setErrors(null)
    if (!invoice || !shipment || !amount || !category || !reason) {
      setErrors('Fill in all required fields.')
      return
    }
    if (!Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      setErrors('Disputed amount must be a positive number.')
      return
    }
    createDispute.mutate()
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      className="fixed inset-0 bg-ink-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-xl shadow-pop max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-ink-200 flex items-center justify-between">
          <div>
            <div className="text-[18px] font-bold">{t('disputes.new_dispute')}</div>
            <div className="text-[12px] text-ink-500 mt-0.5">Open a dispute against a carrier for an overcharge.</div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Carrier *">
              <select value={carrier} onChange={e => setCarrier(e.target.value)} className="finput">
                <option value="DHL">DHL</option>
                <option value="USPS">USPS</option>
                <option value="FEDEX">FedEx</option>
                <option value="UPS">UPS</option>
                <option value="EVRI">Evri</option>
                <option value="ASENDIA">Asendia</option>
                <option value="APC">APC</option>
                <option value="EPOST">EPost</option>
                <option value="INTELCOM">Intelcom</option>
                <option value="UNIUNI">UniUni</option>
                <option value="GOFO">GOFO</option>
              </select>
            </Field>
            <Field label="Invoice number *">
<input className="finput" value={invoice} onChange={e => setInvoice(e.target.value)} placeholder={t('disputes.invoice_placeholder')} />
            </Field>
            <Field label="Tracking / shipment number *">
<input className="finput" value={shipment} onChange={e => setShipment(e.target.value)} placeholder={t('disputes.tracking_placeholder')} />
            </Field>
            <Field label="Disputed amount (USD) *">
              <input className="finput" type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
            </Field>
            <Field label="Charge code">
<input className="finput" value={code} onChange={e => setCode(e.target.value)} placeholder={t('disputes.code_placeholder')} />
            </Field>
            <Field label="Charge name">
<input className="finput" value={chargeName} onChange={e => setChargeName(e.target.value)} placeholder={t('disputes.charge_name_placeholder')} />
            </Field>
            <Field label="Category *" className="col-span-2">
              <select value={category} onChange={e => setCategory(e.target.value)} className="finput">
                <option value="">— select category —</option>
<option>{t('disputes.weight_discrepancy')}</option>
<option>{t('disputes.dimensional_weight')}</option>
<option>{t('disputes.address_correction')}</option>
<option>{t('disputes.duplicate_charge')}</option>
<option>{t('disputes.service_not_rendered')}</option>
<option>{t('disputes.fuel_surcharge')}</option>
<option>Residential / commercial misclassification</option>
<option>{t('disputes.other')}</option>
              </select>
            </Field>
            <Field label="Reason / detail *" className="col-span-2">
<textarea className="finput min-h-[80px] resize-y" value={reason} onChange={e => setReason(e.target.value)}
                        placeholder={t('disputes.reason_placeholder')} />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
            <input type="checkbox" checked={submitNow} onChange={e => setSubmitNow(e.target.checked)} />
            Mark as <b>SUBMITTED</b> immediately (otherwise saved as DRAFT)
          </label>

          {errors && (
            <div className="px-3 py-2 rounded-lg bg-flame-50 border border-flame-200 text-flame-700 text-[13px]">{errors}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border border-ink-200 text-ink-700 font-medium text-[13px] hover:bg-ink-50">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createDispute.isPending}
              className="h-9 px-4 rounded-lg bg-flame-500 hover:bg-flame-600 text-white font-semibold text-[13px] disabled:opacity-50"
            >
              {createDispute.isPending ? 'Saving…' : submitNow ? 'Create & submit' : 'Save as draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-1">{label}</div>
      {children}
    </div>
  )
}

// ============================================================
// "Send to carrier" — preview every e-mail exactly as it will go out, then a
// second, explicit click sends it (POST /api/disputes/email/send). One e-mail
// per carrier from info@byelabel.com; sent disputes become SUBMITTED.
// ============================================================
interface EmailDraft {
  carrier: string
  dispute_ids: number[]
  count: number
  total: number
  contact: { to: string[]; cc: string[]; rep_name: string | null } | null
  subject: string
  html: string
  attachment_name: string
  note: string | null
  // declared box that a carrier will reject (placeholder) or should be checked (thin)
  placeholder?: { dispute_id: number; tracking: string; dims: string; kind: 'placeholder' | 'thin' }[]
}

function SendToCarrierModal({ ids: initialIds, onClose, onSent }: { ids: number[]; onClose: () => void; onSent: () => void }) {
  const qc = useQueryClient()
  // the disputes in this send; "Remove these" below takes the flagged ones out
  const [ids, setIds] = useState<number[]>(initialIds)
  // the box warning must be answered (remove or keep) before Send is possible
  const [boxAck, setBoxAck] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)
  const [results, setResults] = useState<{ carrier: string; status: string; count: number; error?: string }[] | null>(null)
  const [sendError, setSendError] = useState<{ error: string; problems?: string[] } | null>(null)

  const preview = useQuery<{ drafts: EmailDraft[]; problems: string[]; preview_hash: string }>({
    queryKey: ['dispute-email-preview', ids],
    queryFn: async () => {
      const r = await fetch('/api/disputes/email/preview', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dispute_ids: ids }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j.error ?? `HTTP ${r.status}`)
      return j
    },
    gcTime: 0,
  })
  const drafts = preview.data?.drafts ?? []
  const problems = preview.data?.problems ?? []
  const boxWarnings = drafts.flatMap(d => d.placeholder ?? [])
  const showBoxPopup = !boxAck && !results && !preview.isLoading && boxWarnings.length > 0
  const canSend = !preview.isLoading && !preview.error && drafts.length > 0 && problems.length === 0
    && (boxAck || boxWarnings.length === 0)
  function removeFlagged() {
    const flagged = new Set(boxWarnings.map(w => w.dispute_id))
    const rest = ids.filter(id => !flagged.has(id))
    if (!rest.length) { onClose(); return }
    setIds(rest); setBoxAck(true)
  }

  async function send() {
    setSending(true); setSendError(null)
    try {
      const r = await fetch('/api/disputes/email/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dispute_ids: ids, confirm: true, preview_hash: preview.data?.preview_hash }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setSendError({ error: j.error ?? `HTTP ${r.status}`, problems: j.problems }); return }
      setResults(j.results ?? [])
      onSent()
    } finally {
      setSending(false); setConfirming(false)
      qc.invalidateQueries({ queryKey: ['disputes'] })
      qc.invalidateQueries({ queryKey: ['disputes-summary'] })
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between">
          <div className="text-[15px] font-semibold">Send dispute e-mail to carrier</div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4" /></button>
        </div>

        {showBoxPopup && (
          <div className="absolute inset-0 z-10 bg-black/40 rounded-xl flex items-center justify-center p-4">
            <div role="alertdialog" aria-labelledby="box-warn-title"
              className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-5 space-y-3 text-[13px]">
              <div id="box-warn-title" className="text-[15px] font-semibold text-flame-600">
                Check the declared box before sending
              </div>
              <p className="text-ink-600">
                The customer declared a box size the carrier will not accept as evidence. A size or weight dispute
                with a placeholder box (like 1 x 1 x 1 or 0 x 0 x 0) is usually rejected.
              </p>
              <div className="max-h-56 overflow-y-auto border border-ink-200 rounded-lg divide-y divide-ink-100">
                {boxWarnings.map(w => (
                  <div key={w.dispute_id} className="px-3 py-2 flex items-center gap-3">
                    <span className={`pill text-[11px] ${w.kind === 'placeholder' ? 'pill-flame' : 'pill-honey'}`}>
                      {w.kind === 'placeholder' ? 'Placeholder' : 'Very thin'}
                    </span>
                    <span className="mono text-[12px]">{w.tracking}</span>
                    <span className="ml-auto text-ink-600">{w.dims}</span>
                  </div>
                ))}
              </div>
              <p className="text-[12px] text-ink-500">
                "Very thin" (one side under 0.5 in) can be a real flat mailer; check it. "Placeholder" is almost never a real box.
              </p>
              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <button onClick={onClose} className="h-8 px-4 rounded-lg border border-ink-200 text-[12.5px]">Cancel</button>
                <button onClick={() => setBoxAck(true)}
                  className="h-8 px-4 rounded-lg border border-flame-100 text-flame-600 text-[12.5px] font-semibold hover:bg-flame-50">
                  Keep them and continue
                </button>
                <button onClick={removeFlagged}
                  className="h-8 px-4 rounded-lg bg-ink-900 text-white text-[12.5px] font-semibold hover:bg-ink-700">
                  Remove these {boxWarnings.length} and continue
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-5 overflow-y-auto space-y-4 text-[13px]">
          {preview.isLoading && <div className="text-ink-500">Building the e-mails…</div>}
          {preview.error && <div className="text-flame-600">{(preview.error as Error).message}</div>}

          {results ? (
            <div className="space-y-2">
              {results.map(r => (
                <div key={r.carrier} className={`rounded-lg px-3 py-2 ${r.status === 'SENT' && !r.error ? 'bg-mint-50 text-mint-600' : 'bg-flame-50 text-flame-600'}`}>
                  <b>{r.carrier}</b>: {r.status === 'SENT' && !r.error ? `sent - ${r.count} dispute(s) marked submitted` : r.error ?? r.status}
                </div>
              ))}
            </div>
          ) : (
            <>
              {problems.length > 0 && (
                <div className="rounded-lg bg-flame-50 text-flame-600 px-3 py-2">
                  <div className="font-semibold">Fix these first - nothing can be sent yet:</div>
                  <ul className="list-disc ml-5 mt-1">{problems.map(p => <li key={p}>{p}</li>)}</ul>
                  <div className="mt-1 text-[12px]">Carrier contacts are on Settings → Carrier dispute contacts.</div>
                </div>
              )}
              {drafts.map(d => (
                <div key={d.carrier} className="border border-ink-200 rounded-lg">
                  <div className="px-3 py-2 bg-ink-50 border-b border-ink-200 text-[12.5px] space-y-0.5">
                    <div><b>{d.carrier}</b> · {d.count} dispute(s) · ${d.total.toFixed(2)}</div>
                    <div>To: {d.contact ? d.contact.to.join(', ') : <span className="text-flame-600">no contact</span>}
                      {d.contact?.cc.length ? ` · Cc: ${d.contact.cc.join(', ')}` : ''}</div>
                    <div>Subject: {d.subject}</div>
                    <div className="text-ink-500">Attachment: {d.attachment_name}</div>
                    {d.note && <div className="text-honey-600">{d.note}</div>}
                  </div>
                  {/* the HTML was built server-side with every value escaped */}
                  <div className="p-3 overflow-x-auto" dangerouslySetInnerHTML={{ __html: d.html }} />
                </div>
              ))}
            </>
          )}
          {sendError && (
            <div className="rounded-lg bg-flame-50 text-flame-600 px-3 py-2">
              {sendError.error}
              {sendError.problems && <ul className="list-disc ml-5 mt-1">{sendError.problems.map(p => <li key={p}>{p}</li>)}</ul>}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-100 flex items-center justify-end gap-2">
          {results ? (
            <button onClick={onClose} className="h-8 px-4 rounded-lg bg-ink-900 text-white font-semibold text-[12.5px]">Close</button>
          ) : !confirming ? (
            <>
              <button onClick={onClose} className="h-8 px-4 rounded-lg border border-ink-200 text-[12.5px]">Cancel</button>
              <button disabled={!canSend} onClick={() => setConfirming(true)}
                className="h-8 px-4 rounded-lg bg-royal-600 hover:bg-royal-700 text-white font-semibold text-[12.5px] disabled:opacity-40">
                Send {drafts.length} e-mail{drafts.length === 1 ? '' : 's'}…
              </button>
            </>
          ) : (
            <>
              <span className="text-[12.5px] text-ink-600 mr-auto">
                Send {drafts.length} e-mail{drafts.length === 1 ? '' : 's'} from info@byelabel.com now? The disputes will be marked submitted.
              </span>
              <button onClick={() => setConfirming(false)} disabled={sending} className="h-8 px-4 rounded-lg border border-ink-200 text-[12.5px]">Back</button>
              <button onClick={send} disabled={sending}
                className="h-8 px-4 rounded-lg bg-flame-600 hover:bg-flame-700 text-white font-semibold text-[12.5px] disabled:opacity-50">
                {sending ? 'Sending…' : 'Yes, send'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
